import React, { useEffect, useState } from "react";
import styled from "styled-components";

import LoadingPage from "@/components/LoadingPage";
import { getErrorMessage } from "@/lib/error";
import type { MondayService } from "@/services/monday-service";
import type { EnsureOAuthTokenFunc } from "@/services/oauth-service";

import { useCurrentMondayContext } from "./WithMondayContextAvailable";

interface Props {
  children: React.ReactNode;
  ensureOAuthToken: EnsureOAuthTokenFunc;
  mondayService: MondayService;
}

// This component is responsible for ensuring that the CLI backend has stored an OAuth token
// If so, it will display the child components (our app content) else it will trigger the OAuth flow.
export default function WithOAuthEnabled({ children, ensureOAuthToken, mondayService }: Props) {
  const [isLoading, setIsLoading] = useState(true);
  const [isHasToken, setHasToken] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const {
    app: { clientId },
  } = useCurrentMondayContext();

  useEffect(() => {
    void (async () => {
      try {
        const sessionToken = await mondayService.getSessionToken();
        const accountSlug = await mondayService.getAccountSlug();
        if (sessionToken && accountSlug) {
          const hasToken = await ensureOAuthToken(clientId, sessionToken, accountSlug);
          if (hasToken) {
            setHasToken(true);
          }
        }
      } catch (error) {
        setErrorMessage(getErrorMessage(error));
      } finally {
        setIsLoading(false);
      }
    })()
      .then(() => {})
      .catch(() => {});
  }, [clientId]);

  if (isLoading) {
    return <LoadingPage label="Loading..." />;
  }
  if (!isHasToken) {
    const errorText = errorMessage ? `: ${errorMessage}` : "";
    return errorMessage ? (
      <Center>
        <div>{`An error has occurred${errorText}`}</div>
      </Center>
    ) : null;
  }

  return <>{children}</>;
}

const Center = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;
